import React, { useEffect, useState, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { paymentMethods } from 'constants/dropdownOptions';
import { ModalHeader } from 'shared/ModalHeader';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

import { useForm, Controller } from 'react-hook-form';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { getClientUserList } from 'services/case/caseServices';
import { disburseTrustFund } from 'services/billingServices';
import { confirmDialog } from 'primereact/confirmdialog';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';

export const DisburseFundModal = ({ onHide, caseDetails, callback }) => {
  const { addToast } = useToast();
  const [billingContacts, setBillingContacts] = useState([]);
  const userContext = useContext(UserDetailsContext);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      amount: undefined,
      refundDate: undefined,
      paymentMethod: undefined,
      notes: '',
    },
  });

  const getCaseContacts = () => {
    getClientUserList({ caseId: caseDetails?.case_id, is_contact_list: true })
      .then((response) => {
        let user_list = [];
        if (response?.data && response.data.length > 0) {
          response.data.forEach((index) => {
            let name = index.first_name ? index.first_name : '';
            if (index.last_name) name = name + ' ' + index.last_name;
            let obj = {
              contact_id: index.contact_id,
              name: name,
              is_confirmed: index.is_confirmed,
              first_name: index.first_name,
            };
            user_list.push(obj);
          });
        }
        // Sort contacts alphabetically by first_name
        const sortedContacts = user_list.sort((a, b) => {
          const nameA = (a?.first_name || '').toLowerCase();
          const nameB = (b?.first_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setBillingContacts([...sortedContacts]);
      })
      .catch((err) => {
        console.log('err', err);
      });
  };

  useEffect(() => {
    getCaseContacts();
  }, []);

  const onChangeRefundFull = (e) => {
    if (e.target.checked && caseDetails?.waiting_payments?.running_trust_balance) {
      setValue('amount', parseFloat(caseDetails?.waiting_payments?.running_trust_balance), { shouldValidate: true });
    }
    if (!e.target.checked) {
      setValue('amount', undefined);
    }
  };

  const onSubmit = (formData) => {
    confirmDialog({
      message: 'Editing and refunding options will be disabled for all trust transactions. Are you sure you want to proceed?',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const data = {
          case_id: caseDetails?.case_id,
          case_name: caseDetails?.case_description?.case_name,
          billing_contact_id: formData?.billing_contact?.contact_id,
          billing_contact_name: formData?.billing_contact?.name,
          description: formData?.notes,
          deposit_into: '',
          debit_account_name: caseDetails?.default_trust_account?.[0]?.account_name,
          debit_account_id: caseDetails?.default_trust_account?.[0]?.account_id,
          payment_record_amount: formData?.amount,
          payment_record_date: setTimezoneWithoutAlteringTime(formData?.refundDate, userContext?.userDetails?.timezone),
          payment_method: formData?.paymentMethod,
          payment_record_type: 'REFUND',
        };
        disburseTrustFund(data)
          .then((response) => {
            if (response?.status === 200) {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DISBURSE_FUND_SUCCESS);
              typeof callback === 'function' && callback(true);
              onHide();
            } else {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DISBURSE_FUND_FAILED);
            }
          })
          .catch((error) => {
            console.log('error', error);
            addToast(
              false,
              toastConstant.toasterType.ERROR,
              toastConstant.api.FAILED,
              error?.response?.data?.message || toastConstant.message.DISBURSE_FUND_FAILED
            );
          });
      },
      reject: () => null,
    });
  };

  const amount = watch('amount');

  return (
    <Dialog header={ModalHeader} visible onHide={onHide} className="create-msg-popup" closable={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
            <div className="modal-heading F-size16 text-bold">Withdraw Fund</div>
          </div>

          <div className="d-flex py-2 flex-wrap">
            <div className="col-12 col-md-4 pt-2">
              Withdraw Amount<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Required Valid Amount',
                  min: { value: 0, message: 'Must be greater than 0' },
                  max: {
                    value: parseFloat(caseDetails?.waiting_payments?.running_trust_balance),
                    message: 'Cannot be greator that available trust balance',
                  },
                }}
                render={({ field: { name, onChange, value } }) => (
                  <InputNumber
                    name={name}
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    className="input-shadow w-100"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                  />
                )}
              />
              <RequiredValidation field="amount" errors={errors} />
              <div className="mt-3">
                <span className="me-2">Withdraw full amount</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={amount === parseFloat(caseDetails?.waiting_payments?.running_trust_balance)}
                    onChange={onChangeRefundFull}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4 p-medium">
              Contact<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="billing_contact"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <Dropdown
                    className="w-100"
                    options={billingContacts}
                    onChange={(e) => field.onChange(e.value)}
                    optionLabel="name"
                    filterBy="name"
                    placeholder={'Select Contact'}
                    value={field.value}
                    filter
                  />
                )}
              />
              <RequiredValidation field="billing_contact" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4 p-medium">
              Payment Method<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="paymentMethod"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown value={value} onChange={onChange} className="input-shadow w-100 p-0" options={paymentMethods} filter />
                )}
              />
              <RequiredValidation field="paymentMethod" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4">
              Date<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="refundDate"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => <Calendar value={value} onChange={onChange} className="input-shadow w-100" />}
              />
              <RequiredValidation field="refundDate" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4">Notes</div>
            <div className="col-12 col-md-8">
              <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextSnippetWrapper
                    value={value}
                    onChange={onChange}
                    type="textarea"
                    className="input-shadow w-100 p-2"
                    autoComplete="off"
                  />
                )}
              />
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-12 d-flex justify-content-end mt-2">
            <Button className="p-button-secondary outline" label="Cancel" type="button" onClick={onHide} />
            <Button className="p-button-secondary ms-2" label="Withdraw" type="submit" />
          </div>
        </div>
      </form>
    </Dialog>
  );
};
